import React, { useEffect, useMemo, useState } from 'react';
import { useAPIWrapper, DetailBySchema } from 'drf-react-by-schema';
import { useNavigate } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';

import Box from '@mui/material/Box';

import { useAppSelector } from 'src/app/hooks';
import { setPage } from 'src/features/pageSlice';
import { appDispatch } from 'src/utils/utils';

import AsmubipButton from 'src/components/AsmubipButton';
import { detailNonAccordion } from 'src/styles/layout';
import { Perfil } from 'src/constants/Perfil';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import DialogActions from '@mui/material/DialogActions';
import { Layout } from 'src/styles';
import { getProductionKeyLabel, ProductionKey } from 'src/constants';
import ProductionChip from 'src/components/ProductionChip';

const MODEL = 'perfil';

export default function PerfilDetail() {
  const { formValues, perfil, key, id, isMobile } = useAppSelector((state) => state.page);
  const { pageForm, loadSinglePageData } = useAPIWrapper();
  const [alertOpen, setalertOpen] = useState(false);
  const navigate = useNavigate();

  const perfilId = useMemo(() => (perfil ? `${perfil.id}` : undefined), [perfil]);
  const formValuesPerfil = useMemo(() => {
    if (!key || !perfil?.tipo || !formValues?.tipo) {
      return;
    }
    return key === 'perfil' ? perfil?.tipo : (formValues?.tipo.value as Perfil | undefined);
  }, [key, perfil, formValues?.tipo]);

  const fieldsLayout = useMemo(() => {
    if (!key || !formValuesPerfil) {
      return;
    }
    const dadosPessoais = {
      title: 'Dados pessoais',
      rows: ['nome', 'cpf', 'apelido', 'data_nascimento', 'telefone', 'telefone_adicional', 'foto'],
      ...detailNonAccordion,
    };

    switch (formValuesPerfil) {
      case Perfil.QUEBRADEIRA:
      case Perfil.DIRECAO:
        return [
          dadosPessoais,
          {
            title: 'Território e grupo',
            rows: ['comunidade', 'grupo', 'is_coordinator_grupo'],
            ...detailNonAccordion,
          },
          {
            title: key === 'gerenciar_usuarias' ? 'Na ASMUBIP' : 'Você na ASMUBIP',
            rows: [
              'ano_inicio_atividade',
              'local_coleta',
              'distancia_local_coleta',
              'data_filiacao',
              'is_coordinator_grupo',
              'is_associated_asmubip',
              // 'tipo',
            ],
            ...detailNonAccordion,
          },
        ];
      case Perfil.ENTREPOSTO:
        return [
          dadosPessoais,
          {
            title: 'Território e entreposto',
            rows: ['comunidade', 'entreposto', 'is_manager_entreposto'],
            ...detailNonAccordion,
          },
        ];
      case Perfil.ASSESSORIA:
        return [
          dadosPessoais,
          {
            title: 'Entidade',
            rows: ['entidade_assessoria'],
            ...detailNonAccordion,
          },
        ];
      case Perfil.PARCEIRO:
        return [
          dadosPessoais,
          {
            title: 'Parceria',
            rows: ['entidade_assessoria', 'tipo_parceiro'],
            ...detailNonAccordion,
          },
        ];
      case Perfil.FAMILIAR:
        return [
          dadosPessoais,
          {
            title: 'Referência',
            rows: ['quebradeira_referencia'],
            ...detailNonAccordion,
          },
        ];
      case Perfil.SUPERADMIN:
        return [dadosPessoais];
    }
  }, [formValuesPerfil, key]);

  const handleEditClick = () => {
    if (!id) {
      return;
    }
    navigate(`/${key}/edit/${id}`);
  };

  const handleAlertClose = () => {
    setalertOpen(false);
  };

  const handleAlertOpen = () => {
    setalertOpen(true);
  };

  const handleChangeOwnPassword = () => {
    handleAlertClose();
    navigate(`/minha_senha/edit/${perfilId}`);
  };

  const handleChangePassword = () => {
    handleAlertClose();
    navigate(`/change_password/edit/${id}`);
  };

  useEffect(() => {
    const loadAllDataAsync = async () => {
      if (id === null || !perfilId) {
        return;
      }

      const values = await loadSinglePageData({
        model: MODEL,
        objId: id,
      });
      if (values) {
        const pageTitle = key === 'gerenciar_usuarias' ? values.nome : 'Seu Perfil';
        appDispatch(
          setPage({
            id: id,
            model: MODEL,
            formValues: values,
            title: pageTitle,
          })
        );
      }
    };
    loadAllDataAsync();
  }, [id, key, perfilId]);

  if (!pageForm?.schema || !pageForm.columns || !formValues || !fieldsLayout) {
    return null;
  }

  return (
    <>
      <Box sx={Layout.topBarOverListWithScrollX}>
        {Object.values(ProductionKey).map((productionKey) => (
          <ProductionChip
            key={`filter_${productionKey}`}
            type={productionKey}
            label={`Ver ${getProductionKeyLabel({ key: productionKey, isTitle: true })}`}
            onClick={() => {
              navigate(`/${productionKey}_production/?perfil=${formValues.id}`);
            }}
            size="medium"
          />
        ))}
      </Box>
      <DetailBySchema
        schema={pageForm.schema}
        columns={pageForm.columns}
        values={formValues}
        fieldsLayout={fieldsLayout}
        elevation={0}
      />
      <Box
        sx={
          isMobile
            ? { paddingBottom: 2, display: 'flex', flexDirection: 'column', gap: 4 }
            : {
                display: 'flex',
                justifyContent: 'flex-end',
                paddingBottom: 2,
                marginTop: 2,
                gap: 4,
              }
        }>
        <AsmubipButton
          variant="contained-secondary"
          fullWidth={isMobile}
          aria-label="editar"
          size="large"
          onClick={handleEditClick}
          startIcon={<EditIcon />}>
          Editar
        </AsmubipButton>
        {id === perfilId ? (
          <AsmubipButton
            variant="outlined"
            fullWidth={isMobile}
            aria-label="editar"
            size="large"
            onClick={handleAlertOpen}>
            Mudar sua senha
          </AsmubipButton>
        ) : (
          <AsmubipButton
            variant="outlined"
            fullWidth={isMobile}
            aria-label="editar"
            size="large"
            onClick={handleChangePassword}>
            Mudar a senha
          </AsmubipButton>
        )}
      </Box>
      <Dialog open={alertOpen} onClose={handleAlertClose} maxWidth="md" fullWidth>
        <DialogTitle>Atenção!</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1">Tem certeza que quer mudar a sua senha?</Typography>
        </DialogContent>
        <DialogActions>
          <AsmubipButton variant="outlined" onClick={handleAlertClose}>
            Cancelar
          </AsmubipButton>
          <AsmubipButton variant="contained-dark" onClick={handleChangeOwnPassword}>
            Mudar senha
          </AsmubipButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
